import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import IconDown from '../../icons/down';
import IconUp from '../../icons/up';
import IconChecked from '../../icons/checked';
import IconExpand from '../../icons/expand';
import IconChangeLoClock from '../../icons/change-log-clock';
import Select from '../../select/select';
import ButtonPrimary from '../../button/button-primary';
import { ButtonSize } from '../../button/types';
import Textarea from '../../textarea/textarea';
import RequestBlock from './request-block';
import {
  TransactionWarningActionEnum,
  MatchStatusEnum,
  BlacklistUserInfoData,
} from '../types';
import formatDate from '../../../helpers/format-date';
import { PermissionEnum } from '../../../base-types';
import IconRefresh from '../../icons/refresh';
import ButtonSecondary from '../../button/button-secondary';
import Popup from '../../popup/popup';
import { TransactionBlacklistWarningTypes } from './types';
import IconLoader from '../../icons/loader';
import formatBlacklistItem from '../../../helpers/format-blacklist-warning-item';

import styles from '../transaction.module.css';

export default function TransactionBlacklistWarning({
  warning,
  employee,
  onApplyTransactionAction,
  setBlacklistDataVisible,
  setBlacklistData,
  setBlacklistItemVisible,
  setBlacklistDataItem,
  applyTransactionsWarningIsLoading,
  internal,
  companyOfficials,
  recipients,
  senders,
  individualCustomer,
  recipientAccount,
  senderAccount,
  chats,
  createChatWithMessage,
  chatCreateInProgress,
  activePermissions,
  withoutChat,
  hideApproveActions,
  hideRejectActions,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  warningInGroupId,
  withoutActions,
  getWarningById,
  entityId,
  isWarningByIdFetching,
  isWarningByIdLoading,
  globalCollapsed,
  setHideWarningsIds,
  hideWarningsIds,
  isPaymentOrder,
  withWhiteBackground,
  warningsReport,
  warningActionError,
}: TransactionBlacklistWarningTypes) {
  const [collapsed, setCollapsed] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRequest, setSelectedRequest] = useState('');
  const [requestText, setRequestText] = useState('');
  const [flagText, setFlagText] = useState('');
  const [matchStatus, setMatchStatus] = useState('');
  const [resetPopupVisible, setResetPopupVisible] = useState(false);

  const onApply = () => {
    if (onApplyTransactionAction) {
      onApplyTransactionAction({
        body: {
          action,
          comment: flagText,
          employeeId: employee?.id as string,
          matchStatus,
        },
        warningId: warning?.id as string,
      });
    }
  };

  useEffect(() => {
    if (isPaymentOrder) {
      setCollapsed(!globalCollapsed);
    }
  }, [globalCollapsed, isPaymentOrder]);

  useEffect(() => {
    if (warning?.action) {
      setAction(warning.action);
    }
  }, [warning?.action]);

  useEffect(() => {
    if (warning?.matchStatus) {
      setMatchStatus(warning.matchStatus);
    }
  }, [warning?.matchStatus]);

  useEffect(() => {
    if (warning?.comment) {
      setFlagText(warning.comment);
    }
  }, [warning?.comment]);

  useEffect(() => {
    if (resetTransactionsWarningIsSuccess && !warningInGroupId) {
      setResetPopupVisible(false);
      setAction('');
      setFlagText('');
      setMatchStatus('');
    }
  }, [resetTransactionsWarningIsSuccess, warningInGroupId]);

  useEffect(() => {
    if (
      resetTransactionsWarningIsSuccess &&
      warningInGroupId &&
      warningInGroupId === warning?.id
    ) {
      setResetPopupVisible(false);
      setAction('');
      setFlagText('');
      setMatchStatus('');
    }
  }, [warningInGroupId, resetTransactionsWarningIsSuccess]);

  let warningIcon = <div className={styles.circle} />;

  if (warning?.action) {
    warningIcon = (
      <div className={styles.checked}>
        <IconChecked />
      </div>
    );

    if (warning.action === 'STOP') {
      warningIcon = (
        <div className={styles.declined}>
          <IconExpand />
        </div>
      );
    }
  }

  const onSetFormattedBlacklistItemData = (
    blacklistData: BlacklistUserInfoData,
  ) => {
    if (setBlacklistDataItem) {
      setBlacklistDataItem(formatBlacklistItem(blacklistData));
    }
  };

  let collapsedContent;

  if (
    (isWarningByIdFetching || isWarningByIdLoading) &&
    warning?.id &&
    !warning?.blacklistDoc &&
    !warning?.blacklistItem &&
    !isPaymentOrder
  ) {
    collapsedContent = (
      <div className={styles.warningLoader}>
        <IconLoader />
      </div>
    );
  } else {
    collapsedContent = (
      <>
        <div className={styles.blacklistInfoName}>
          {warning?.blacklist?.name}
        </div>
        <div className={styles.blacklistInfoBlock}>
          <div className={styles.blacklistInfoBlockLeft}>
            <div className={styles.blacklistInfoBlockTitle}>
              Matching Result:
            </div>
            <div className={styles.blacklistInfoBlockValue}>
              {warning?.blacklistMatchResult}
            </div>
          </div>

          <div className={styles.blacklistInfoBlockRight}>
            <button
              className={styles.blacklistLink}
              type="button"
              onClick={() => {
                if (setBlacklistDataVisible && warning?.blacklistDoc) {
                  setBlacklistDataVisible(true);
                }

                if (setBlacklistData && warning?.blacklistDoc) {
                  setBlacklistData(warning?.blacklistDoc);
                }

                if (setBlacklistItemVisible && warning?.blacklistItem) {
                  setBlacklistItemVisible(true);
                }

                if (setBlacklistDataItem && warning?.blacklistItem) {
                  onSetFormattedBlacklistItemData(warning?.blacklistItem);
                }

                if (
                  getWarningById &&
                  !warning?.blacklistDoc &&
                  !warning?.blacklistItem
                ) {
                  getWarningById({
                    id: entityId as string,
                    warningId: warning?.id as string,
                  });
                }
              }}
            >
              {(isWarningByIdFetching || isWarningByIdLoading) &&
              warning?.id &&
              !warning?.blacklistDoc &&
              !warning?.blacklistItem ? (
                <IconLoader width={16} height={16} />
              ) : (
                'Details'
              )}
            </button>
          </div>
        </div>
        <div className={styles.blacklistInfoBlock}>
          <div className={styles.blacklistInfoBlockLeft}>
            <div className={styles.blacklistInfoBlockTitle}>Matching Part:</div>
            <div className={styles.blacklistInfoBlockValue}>
              {warning?.blacklistMatchingPart}
            </div>
          </div>
        </div>
        <div className={`${styles.blacklistInfoBlock} ${styles.moreMargin}`}>
          <div className={styles.blacklistInfoBlockLeft}>
            <div className={styles.blacklistInfoBlockTitle}>Match:</div>
            <div className={styles.blacklistInfoBlockValue}>
              {`${warning?.blacklistMatchScore}%`}
            </div>
          </div>
        </div>
        <div className={`${styles.blacklistInfoBlock} ${styles.moreMargin}`}>
          <div className={styles.blacklistInfoBlockLeft}>
            <div className={styles.blacklistInfoBlockTitle}>
              Blacklist description:
            </div>
            <div className={styles.blacklistInfoBlockValue}>
              {warning?.blacklist?.description}
            </div>
          </div>
        </div>
        {warning?.blacklistDoc && (
          <div
            className={styles.blaklistDoc}
          >{`Doc ID: ${warning?.blacklistDoc.id}`}</div>
        )}
        {activePermissions?.includes(PermissionEnum.CHANNELS_LIST) &&
          activePermissions?.includes(PermissionEnum.CHANNELS_CREATE_ACTION) &&
          !withoutChat &&
          !warningsReport && (
            <RequestBlock
              setRequestText={setRequestText}
              selectedRequest={selectedRequest}
              requestText={requestText}
              setSelectedRequest={setSelectedRequest}
              action={warning?.action}
              internal={internal}
              companyOfficials={companyOfficials}
              recipients={recipients}
              senders={senders}
              individualCustomer={individualCustomer}
              recipientAccount={recipientAccount}
              senderAccount={senderAccount}
              chats={chats}
              createChatWithMessage={createChatWithMessage}
              chatCreateInProgress={chatCreateInProgress}
              withoutBorder
            />
          )}
        {!withoutActions && (
          <div
            className={`${styles.blacklistActionWrapper} ${
              warning?.action ? styles.withoutBackground : ''
            }`}
          >
            {(onApplyTransactionAction || warning?.action) && (
              <div className={styles.actionWrapper}>
                <div className={styles.actionWrapperTitle}>
                  Is the Match true?
                </div>
                <Select
                  name="matchStatus"
                  value={matchStatus}
                  onChange={(e) => setMatchStatus(e.target.value as string)}
                  list={[
                    { name: 'False', value: MatchStatusEnum.FALSE },
                    { name: 'True', value: MatchStatusEnum.TRUE },
                    { name: 'Potential', value: MatchStatusEnum.POTENTIAL },
                  ]}
                  placeholder="Select Match status"
                  selectWrapperClassName={`${styles.selectAction} ${styles.withMargin}`}
                  disabled={
                    Boolean(warning?.action) ||
                    applyTransactionsWarningIsLoading
                  }
                  fullWidth
                />
                <div className={styles.actionWrapperTitle}>
                  What to do with a warning?
                </div>
                <Select
                  name="actions"
                  value={action}
                  onChange={(e) => setAction(e.target.value as string)}
                  list={[
                    {
                      name: 'Pass all time for this client',
                      value:
                        TransactionWarningActionEnum.PASS_ALL_TIME_FOR_THIS_CLIENT,
                      hidden: hideApproveActions,
                    },
                    {
                      name: 'Pass combination',
                      value: TransactionWarningActionEnum.PASS_COMBINATION,
                      hidden: hideApproveActions,
                    },
                    {
                      name: 'Pass once',
                      value: TransactionWarningActionEnum.PASS_ONCE,
                      hidden: hideApproveActions,
                    },
                    {
                      name: 'Stop',
                      value: TransactionWarningActionEnum.STOP,
                      hidden: hideRejectActions,
                    },
                  ]}
                  placeholder="Select actions"
                  selectWrapperClassName={styles.selectAction}
                  disabled={
                    Boolean(warning?.action) ||
                    applyTransactionsWarningIsLoading ||
                    (hideApproveActions && hideRejectActions)
                  }
                  fullWidth
                />
              </div>
            )}
            {!warning?.action && !onApplyTransactionAction && (
              <div
                className={`${styles.warningActionApplied} ${styles.resolutionNotSelected}`}
              >
                Resolution not selected
              </div>
            )}
            {warning?.action &&
              warning?.employee &&
              warning?.employee?.profile?.firstName &&
              warning?.employee?.profile?.lastName && (
                <div className={styles.warningActionApplied}>
                  <div className={styles.warningActionAppliedLeft}>
                    Resolved by&nbsp;
                    <span>{`${warning?.employee?.profile?.firstName} ${warning?.employee?.profile?.lastName}`}</span>
                  </div>
                  <span>{formatDate(warning?.updatedAt as string, true)}</span>
                </div>
              )}
            {action && (
              <Textarea
                name="flagText"
                value={warning?.action ? `«${flagText}»` : flagText}
                onChange={(e) => setFlagText(e.target.value)}
                placeholder="Enter a comment..."
                disabled={
                  Boolean(warning?.action) || applyTransactionsWarningIsLoading
                }
                labelClassName={styles.flagTextareaLabel}
                className={warning?.action ? styles.flagTextareaDisabled : ''}
                borderClassName={
                  warning?.action ? styles.flagTextareaBorderDisabled : ''
                }
              />
            )}
            <div className={styles.applyWarningWrapper}>
              {!warning?.action && onApplyTransactionAction && (
                <ButtonPrimary
                  text={
                    applyTransactionsWarningIsLoading
                      ? 'Loading...'
                      : 'Apply action'
                  }
                  className={styles.applyActionButton}
                  size={ButtonSize.SMALL}
                  disabled={
                    !(action && flagText && matchStatus) ||
                    applyTransactionsWarningIsLoading
                  }
                  onClick={onApply}
                />
              )}
              {warning?.action && onResetTransactionWarningAction && (
                <ButtonSecondary
                  text="Reset actions and statuses"
                  icon={<IconRefresh />}
                  size={ButtonSize.SMALL}
                  className={styles.resetWarningStatus}
                  onClick={() => setResetPopupVisible(true)}
                />
              )}
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <NavLink
                  to={`/administration/change-logs?entityType=WARNING&entityId=${warning?.id}`}
                  className={styles.changeLogLink}
                  target="_blank"
                >
                  <div>
                    <IconChangeLoClock />
                    Change Log
                  </div>
                </NavLink>
              )}
            </div>
            {warningActionError &&
              warningActionError.warningId === warning?.id && (
                <div className={styles.warningActionError}>
                  {warningActionError.error}
                </div>
              )}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={`${styles.warningItem} ${
        withWhiteBackground ? styles.whiteBackground : ''
      }`}
    >
      <button
        type="button"
        className={styles.warningItemCollapseButton}
        onClick={() => {
          setCollapsed(!collapsed);

          if (hideWarningsIds && setHideWarningsIds) {
            let nextHideWarningsIds = ([] as string[]).concat(hideWarningsIds);

            if (!collapsed) {
              nextHideWarningsIds = nextHideWarningsIds.filter(
                (item) => item !== warning?.id,
              );
            } else {
              nextHideWarningsIds.push(warning?.id as string);
            }

            setHideWarningsIds(nextHideWarningsIds);
          }

          if (
            !collapsed &&
            !warning?.blacklistDoc &&
            !warning?.blacklistItem &&
            !isPaymentOrder &&
            getWarningById
          ) {
            getWarningById({
              id: entityId as string,
              warningId: warning?.id as string,
            });
          }
        }}
      >
        <div className={styles.buttonLeft}>
          {warningIcon}
          <div className={styles.buttonLeftValue}>
            {warning?.blacklistMatchResult}
          </div>
        </div>
        <div className={styles.buttonRight}>
          <div className={styles.blacklistName}>
            <div>{warning?.blacklist?.name}</div>
          </div>
          <div className={styles.buttonIconWrapper}>
            {collapsed ? <IconUp /> : <IconDown />}
          </div>
        </div>
      </button>
      {collapsed && collapsedContent}
      <Popup
        title="The selected actions and statuses will be canceled"
        onCancelClick={() => setResetPopupVisible(false)}
        onOkClick={() =>
          onResetTransactionWarningAction &&
          onResetTransactionWarningAction(warning?.id as string)
        }
        cancelButtonText="Cancel"
        okButtonText={
          resetTransactionsWarningIsLoading ? 'Loading...' : 'Yes, reset'
        }
        isVisible={resetPopupVisible}
        isLoading={resetTransactionsWarningIsLoading}
        parent={document.getElementById('signature-popup') as HTMLElement}
      />
    </div>
  );
}
